import React, { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react';
import {
  FaPlay,
  FaPause,
  FaBackward,
  FaForward,
  FaVolumeUp,
  FaVolumeDown,
  FaVolumeMute,
  FaCog,
  FaDesktop,
  FaServer,
  FaTv,
  FaPowerOff,
  FaHome,
  FaChevronUp,
  FaChevronDown,
  FaStepBackward,
  FaStepForward,
} from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import DefaultlayoutHOC from '../layouts/Default.layout';
import { MovieContext } from '../components/context/Movies.context';
import { openRemoteChannel, subscribeTvState, probeTv } from '../services/cast';
import { PLAYERS, getStoredPlayerId } from '../services/players';
import JioRemote from '../components/remote/JioRemote';

// ---------------------------------------------------------------------------
// Remote.page - phone = TV controller for the TV app (same 6-digit code).
//
// While nothing is playing, renders a JioSphere-style remote card (optional
// air mouse, horizontal scroll toggle, speed, trackpad + scroll rail).
// Once playback starts, switches to the physical-remote layout.
// ---------------------------------------------------------------------------

// Must stay identical to public/receiver.html's QUALITY_MENU values.
const QUALITY_OPTIONS = [
  { value: 'auto', short: 'Auto' },
  { value: 'hd', short: '1080p' },
  { value: '720', short: '720p' },
  { value: 'sd', short: '480p' },
];

const fmtTime = (s) => {
  const sec = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const x = sec % 60;
  const p = (n) => String(n).padStart(2, '0');
  return `${h ? `${h}:${p(m)}` : `${m}`}:${p(x)}`;
};

const PadButton = ({ label, icon, onClick, className = '' }) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    className={`flex items-center justify-center rounded-2xl text-white transition-all active:scale-95 ${className}`}
  >
    {icon}
  </button>
);

const Chip = ({ active, children, onClick, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-xl px-2 py-2 text-[11px] font-bold border transition-colors ${
      active
        ? 'bg-accent-gold text-dark-900 border-accent-gold'
        : 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/15'
    } ${className}`}
  >
    {children}
  </button>
);

const RemotePage = () => {
  const { castCode, saveTvCode } = useContext(MovieContext);
  const location = useLocation();
  const navigate = useNavigate();
  // QR deep link: /remote?code=123456&bind=1 opens here and pre-fills the code.
  // We only send "connect" once the TV reports its surface, so an active stream
  // is never interrupted and a TV already on Home is not reloaded.
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const rawCode = String(params.get('code') || '').replace(/\D/g, '').slice(0, 6);
  const codeFromUrl = rawCode.length === 6 ? rawCode : '';
  const bindFromUrl = params.get('bind') === '1';

  const [code, setCode] = useState(codeFromUrl || castCode || '');
  const [connected, setConnected] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const [tvState, setTvState] = useState(null);
  const [epSeason, setEpSeason] = useState(1);
  const [epEpisode, setEpEpisode] = useState(1);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mouseMode, setMouseMode] = useState(false);

  const channelRef = useRef(null);
  const stateSubRef = useRef(null);
  const connectGateRef = useRef(false);

  // If the QR link changes the code while this page is already open, follow it.
  // A fresh scan is allowed to re-evaluate the TV surface.
  useEffect(() => {
    connectGateRef.current = false;
    if (codeFromUrl) setCode((prev) => (prev === codeFromUrl ? prev : codeFromUrl));
  }, [codeFromUrl, bindFromUrl]);

  // Probe the TV + subscribe to its live state whenever the code is complete.
  useEffect(() => {
    const clean = String(code || '').replace(/\D/g, '');
    if (clean.length !== 6) {
      setTvState(null);
      setConnected(false);
      setError(null);
      return undefined;
    }
    setChecking(true);
    let alive = false;

    const t = setTimeout(() => {
      alive = true;
      probeTv(clean).then((ok) => {
        if (!alive) return;
        setConnected(ok);
        setChecking(false);
        if (ok) {
          stateSubRef.current = subscribeTvState(clean, (s) => setTvState(s));
        } else {
          setError('TV online nahi hai - TV par /tv page kholo');
        }
      });
    }, 500);

    return () => {
      alive = false;
      clearTimeout(t);
      if (stateSubRef.current) {
        stateSubRef.current.stop();
        stateSubRef.current = null;
      }
    };
  }, [code]);

  // One persistent command channel while connected (no reconnect per tap).
  useEffect(() => {
    if (!connected) return undefined;
    const ch = openRemoteChannel(String(code).replace(/\D/g, ''));
    channelRef.current = ch;
    const ask = setTimeout(() => ch.send({ type: 'state' }), 400);
    return () => {
      clearTimeout(ask);
      ch.stop();
      channelRef.current = null;
    };
  }, [connected, code]);

  // Direct Remote / QR both need the same rule: TV pairing screen -> send
  // connect (TV opens app Home); TV already Home/Player -> don't touch it.
  // Runs once per code/connection because TV state is asynchronous.
  useEffect(() => {
    if (!connected || connectGateRef.current || !tvState) return;
    const surface = tvState.surface || (tvState.path ? 'player' : 'pairing');
    if (surface === 'pairing') {
      const ch = channelRef.current;
      if (!ch) return;
      ch.send({ type: 'connect' });
    }
    connectGateRef.current = true;
    if (saveTvCode) saveTvCode(code);
    if (bindFromUrl) navigate(`/remote?code=${encodeURIComponent(code)}`, { replace: true });
  }, [connected, tvState, code, bindFromUrl, navigate, saveTvCode]);

  // Keep episode pickers roughly in sync with what the TV is playing.
  useEffect(() => {
    if (tvState?.season) setEpSeason(tvState.season);
    if (tvState?.episode) setEpEpisode(tvState.episode);
  }, [tvState]);

  const send = useCallback((cmd) => {
    if (!channelRef.current) return;
    channelRef.current.send(cmd);
  }, []);

  const playing = Boolean(tvState?.path);
  const activePlayer = tvState?.player || getStoredPlayerId();
  // While the video is on, map mouse-pad Back/Home to "stop player". This is
  // intentionally different from idle mode, where they navigate the TV app.
  const playerSend = useCallback(
    (cmd) => {
      if (cmd?.type === 'nav' && (cmd.value === 'back' || cmd.value === 'home')) {
        send({ type: 'stop' });
        return;
      }
      send(cmd);
    },
    [send]
  );

  useEffect(() => {
    if (!playing) setMouseMode(false);
  }, [playing]);

  const seekBy = useCallback((delta) => send({ type: 'seekBy', value: String(delta) }), [send]);
  const setVol = useCallback((v) => send({ type: 'vol', value: String(v) }), [send]);

  const togglePlay = useCallback(() => {
    send({ type: tvState?.playing ? 'pause' : 'resume' });
  }, [send, tvState]);

  const stepVolume = useCallback(
    (delta) => setVol(Math.min(100, Math.max(0, (tvState?.volume ?? 50) + delta))),
    [setVol, tvState]
  );

  const stepEpisode = useCallback(
    (delta) =>
      send({
        type: 'episode',
        season: tvState?.season || epSeason,
        episode: Math.max(1, (tvState?.episode || epEpisode) + delta),
      }),
    [send, tvState, epSeason, epEpisode]
  );

  // D-pad: navigate the TV app while idle, control playback while playing.
  const pressUp = () => (playing ? stepVolume(10) : send({ type: 'nav', value: 'up' }));
  const pressDown = () => (playing ? stepVolume(-10) : send({ type: 'nav', value: 'down' }));
  const pressLeft = () => (playing ? seekBy(-10) : send({ type: 'nav', value: 'left' }));
  const pressRight = () => (playing ? seekBy(10) : send({ type: 'nav', value: 'right' }));
  const pressOk = () => (playing ? togglePlay() : send({ type: 'nav', value: 'select' }));
  const pressBack = () => (playing ? send({ type: 'stop' }) : send({ type: 'nav', value: 'back' }));
  const pressHome = () => (playing ? send({ type: 'stop' }) : send({ type: 'nav', value: 'home' }));

  const progress = useMemo(() => {
    if (!tvState || !tvState.duration) return 0;
    return Math.min(100, Math.round((tvState.position / tvState.duration) * 100));
  }, [tvState]);

  const disconnect = useCallback(() => {
    if (stateSubRef.current) {
      stateSubRef.current.stop();
      stateSubRef.current = null;
    }
    setConnected(false);
    setTvState(null);
  }, []);

  const statusLine = useMemo(() => {
    if (checking) return 'Checking for TV...';
    if (!connected) return 'TV offline - code check karo';
    if (!tvState) return 'TV connected - waiting for state...';
    if (playing) return tvState.playing ? 'Playing on TV' : 'Paused on TV';
    return 'TV Home - touchpad se control karein';
  }, [checking, connected, tvState, playing]);

  return (
    <div className="min-h-screen pt-24 pb-28 px-4 max-w-md mx-auto select-none" style={{ touchAction: 'manipulation' }}>
      {/* Header */}
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-white">TV Remote</h1>
          <p className="text-gray-400 text-xs mt-0.5">{statusLine}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${connected ? 'bg-emerald-400' : 'bg-red-400'} animate-pulse`} />
          <span className="text-xs text-gray-400 font-mono font-bold">{String(code).padEnd(6, '-')}</span>
        </div>
      </div>

      {/* Code entry (only when not connected) */}
      {!connected && (
        <div className="bg-dark-800 border border-white/10 rounded-3xl p-5 mb-5 shadow-xl">
          <label htmlFor="tv-code" className="text-gray-300 text-xs font-bold uppercase tracking-wider mb-2 block">
            TV Pair Code
          </label>
          <input
            id="tv-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            aria-label="TV pairing code"
            className="w-full bg-dark-900 border border-white/15 focus:border-accent-gold/60 rounded-2xl text-center text-3xl font-black tracking-[0.4em] text-white py-4 outline-none transition-colors placeholder:text-gray-600"
          />
          {error && <p className="text-red-400 text-xs font-semibold mt-2 text-center">{error}</p>}
          <p className="text-gray-500 text-[11px] text-center mt-3 leading-relaxed">
            TV par <span className="text-accent-gold font-bold">/tv</span> page kholo - wahan 6-digit code dikhega.
          </p>
        </div>
      )}

      {connected && !playing && (
        <JioRemote
          code={code}
          statusLine={statusLine}
          onCommand={send}
          onDisconnect={disconnect}
        />
      )}

      {connected && playing && mouseMode && (
        <JioRemote
          code={code}
          statusLine="TV Mouse Pad - tap to click"
          onCommand={playerSend}
          onDisconnect={disconnect}
          onSwitch={() => setMouseMode(false)}
          switchLabel="Remote"
        />
      )}

      {connected && playing && !mouseMode && (
        <div className="relative rounded-[2.25rem] border ...">
          {/* Remote top row */}
          <div className="flex items-center justify-between px-1 mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-accent-gold text-dark-900 flex items-center justify-center">
                <FaTv className="w-4 h-4" />
              </div>
              <div className="leading-tight">
                <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">ShadowCinema</p>
                <p className="text-sm font-black text-white">Remote</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMouseMode(true)}
                className="text-[10px] font-bold bg-blue-600 text-white px-3 py-2 rounded-xl active:scale-95 transition-all"
              >
                Mouse Pad
              </button>
              <button
                type="button"
                onClick={() => send({ type: 'stop' })}
                aria-label="Power / stop TV"
                className="p-2.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/25 hover:bg-red-500/25 active:scale-95 transition-all"
              >
                <FaPowerOff className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Now-playing card */}
          <div className="bg-black/35 border border-white/10 rounded-2xl p-3.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-white font-bold text-sm truncate">{tvState?.title || 'ShadowCinema TV'}</p>
                <p className="text-gray-400 text-[11px] mt-0.5">
                  {tvState?.season
                    ? `S${String(tvState.season).padStart(2, '0')}E${String(tvState.episode || 1).padStart(2, '0')}`
                    : playing
                    ? 'Movie'
                    : 'Home'}
                  {tvState ? `  ${fmtTime(tvState.position)} / ${fmtTime(tvState.duration)}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={disconnect}
                className="text-gray-400 hover:text-white text-[10px] font-bold px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10"
              >
                Disconnect
              </button>
            </div>
            <div className="mt-2.5 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-accent-gold rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>

          {/* D-pad */}
          <div className="mt-5 flex justify-center">
            <div className="relative w-60 h-60 rounded-full border border-white/10 bg-black/30 shadow-inner">
              <PadButton
                label={playing ? 'Volume up' : 'Navigate up'}
                onClick={pressUp}
                icon={<FaChevronUp className="w-6 h-6" />}
                className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-14 bg-white/5 hover:bg-white/15"
              />
              <PadButton
                label={playing ? 'Volume down' : 'Navigate down'}
                onClick={pressDown}
                icon={<FaChevronDown className="w-6 h-6" />}
                className="absolute bottom-2 left-1/2 -translate-x-1/2 w-16 h-14 bg-white/5 hover:bg-white/15"
              />
              <PadButton
                label={playing ? 'Rewind 10 seconds' : 'Navigate left'}
                onClick={pressLeft}
                icon={<FaChevronUp className="w-6 h-6 -rotate-90" />}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-14 h-16 bg-white/5 hover:bg-white/15"
              />
              <PadButton
                label={playing ? 'Forward 10 seconds' : 'Navigate right'}
                onClick={pressRight}
                icon={<FaChevronUp className="w-6 h-6 rotate-90" />}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-14 h-16 bg-white/5 hover:bg-white/15"
              />
              <button
                type="button"
                onClick={pressOk}
                aria-label={playing ? 'Play or pause' : 'Select'}
                className="absolute inset-0 m-auto w-24 h-24 rounded-full bg-accent-gold text-dark-900 font-black text-lg shadow-lg active:scale-95 transition-all flex items-center justify-center"
              >
                OK
              </button>
            </div>
          </div>

          <p className="text-center text-[10px] text-gray-500 mt-2">
            {playing ? 'D-pad: volume (up/down) & seek (left/right) - OK: play/pause' : 'D-pad controls the TV app'}
          </p>

          {/* Back / Home */}
          <div className="grid grid-cols-2 gap-2 mt-4">
            <button
              type="button"
              onClick={pressBack}
              className="flex items-center justify-center gap-2 bg-white/5 hover:bg-white/15 text-gray-200 text-xs font-bold py-3 rounded-xl border border-white/10 active:scale-95 transition-all"
            >
              <FaBackward className="w-3.5 h-3.5" /> Back
            </button>
            <button
              type="button"
              onClick={pressHome}
              className="flex items-center justify-center gap-2 bg-white/5 hover:bg-white/15 text-gray-200 text-xs font-bold py-3 rounded-xl border border-white/10 active:scale-95 transition-all"
            >
              <FaHome className="w-3.5 h-3.5" /> Home
            </button>
          </div>

          {/* Volume rocker + play/pause */}
          <div className="mt-4 grid grid-cols-3 gap-2 items-stretch">
            <button
              type="button"
              onClick={() => stepVolume(-10)}
              aria-label="Volume down"
              className="flex flex-col items-center justify-center gap-1 py-4 rounded-2xl bg-white/5 hover:bg-white/15 border border-white/10 text-white active:scale-95 transition-all"
            >
              <FaVolumeDown className="w-5 h-5" />
              <span className="text-[10px] font-bold text-gray-400">VOL -</span>
            </button>
            <button
              type="button"
              onClick={togglePlay}
              aria-label={tvState?.playing ? 'Pause' : 'Play'}
              className="flex flex-col items-center justify-center gap-1 py-4 rounded-2xl bg-accent-gold text-dark-900 shadow-lg active:scale-95 transition-all"
            >
              {tvState?.playing ? <FaPause className="w-6 h-6" /> : <FaPlay className="w-6 h-6" />}
              <span className="text-[10px] font-black">{tvState?.playing ? 'PAUSE' : 'PLAY'}</span>
            </button>
            <button
              type="button"
              onClick={() => stepVolume(10)}
              aria-label="Volume up"
              className="flex flex-col items-center justify-center gap-1 py-4 rounded-2xl bg-white/5 hover:bg-white/15 border border-white/10 text-white active:scale-95 transition-all"
            >
              <FaVolumeUp className="w-5 h-5" />
              <span className="text-[10px] font-bold text-gray-400">VOL +</span>
            </button>
          </div>

          {/* Mute + seek row */}
          <div className="mt-2 grid grid-cols-5 gap-2">
            <button
              type="button"
              onClick={() => send({ type: tvState?.muted ? 'unmute' : 'mute' })}
              aria-label={tvState?.muted ? 'Unmute' : 'Mute'}
              className="flex items-center justify-center py-2.5 rounded-xl bg-white/5 hover:bg-white/15 border border-white/10 text-white active:scale-95 transition-all"
            >
              <FaVolumeMute className={`w-4 h-4 ${tvState?.muted ? 'text-red-400' : ''}`} />
            </button>
            <button type="button" onClick={() => seekBy(-60)} aria-label="Rewind 60 seconds" className="flex items-center justify-center py-2.5 rounded-xl bg-white/5 hover:bg-white/15 border border-white/10 text-white active:scale-95 transition-all">
              <FaStepBackward className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => seekBy(-10)} aria-label="Rewind 10 seconds" className="flex items-center justify-center py-2.5 rounded-xl bg-white/5 hover:bg-white/15 border border-white/10 text-white active:scale-95 transition-all">
              <FaBackward className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => seekBy(10)} aria-label="Forward 10 seconds" className="flex items-center justify-center py-2.5 rounded-xl bg-white/5 hover:bg-white/15 border border-white/10 text-white active:scale-95 transition-all">
              <FaForward className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => seekBy(60)} aria-label="Forward 60 seconds" className="flex items-center justify-center py-2.5 rounded-xl bg-white/5 hover:bg-white/15 border border-white/10 text-white active:scale-95 transition-all">
              <FaStepForward className="w-4 h-4" />
            </button>
          </div>

          {/* Settings */}
          <button
            type="button"
            onClick={() => setSettingsOpen((v) => !v)}
            aria-expanded={settingsOpen}
            className="mt-4 w-full flex items-center justify-between px-3.5 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-gray-200 text-xs font-bold transition-colors"
          >
            <span className="flex items-center gap-2">
              <FaCog className="w-3.5 h-3.5 text-accent-gold" /> Player &amp; Playback Settings
            </span>
            {settingsOpen ? <FaChevronUp className="w-3 h-3" /> : <FaChevronDown className="w-3 h-3" />}
          </button>

          {settingsOpen && (
            <div className="mt-2 bg-black/30 border border-white/10 rounded-2xl p-3 space-y-3">
              <div>
                <p className="text-gray-500 text-[10px] font-bold uppercase mb-1.5 flex items-center gap-1">
                  <FaTv className="w-2.5 h-2.5" /> Player
                </p>
                <div className="grid grid-cols-3 gap-1.5">
                  {PLAYERS.map((player) => (
                    <Chip
                      key={player.id}
                      active={activePlayer === player.id}
                      onClick={() => send({ type: 'player', value: player.id })}
                    >
                      {player.name}
                    </Chip>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-gray-500 text-[10px] font-bold uppercase mb-1.5">Quality</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {QUALITY_OPTIONS.map((q) => (
                    <Chip
                      key={q.value}
                      active={(tvState?.quality || 'auto') === q.value}
                      onClick={() => send({ type: 'quality', value: q.value })}
                    >
                      {q.short}
                    </Chip>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-gray-500 text-[10px] font-bold uppercase mb-1.5 flex items-center gap-1">
                    <FaServer className="w-2.5 h-2.5" /> Server
                  </p>
                  <div className="grid grid-cols-5 gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Chip key={n} active={tvState?.server === n} onClick={() => send({ type: 'server', value: String(n) })}>
                        {n}
                      </Chip>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-gray-500 text-[10px] font-bold uppercase mb-1.5 flex items-center gap-1">
                    <FaDesktop className="w-2.5 h-2.5" /> Source
                  </p>
                  <div className="grid grid-cols-4 gap-1">
                    {[1, 2, 3, 4].map((n) => (
                      <Chip key={n} active={tvState?.source === n} onClick={() => send({ type: 'source', value: String(n) })}>
                        {n}
                      </Chip>
                    ))}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => send({ type: 'nextSource' })}
                className="w-full bg-white/5 hover:bg-white/15 text-gray-200 text-[11px] font-bold py-2 rounded-xl border border-white/10 transition-colors"
              >
                Next Source
              </button>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-gray-500 text-[10px] font-bold uppercase mb-1.5">Audio</p>
                  <div className="grid grid-cols-3 gap-1">
                    {['', 'hindi', 'english'].map((a) => (
                      <Chip
                        key={a || 'def'}
                        active={(tvState?.audio || '') === a}
                        onClick={() => send({ type: 'audio', value: a })}
                      >
                        {a ? a.toUpperCase() : 'DEF'}
                      </Chip>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-gray-500 text-[10px] font-bold uppercase mb-1.5">Subtitles</p>
                  <div className="grid grid-cols-3 gap-1">
                    {['off', 'english', 'hindi'].map((sl) => (
                      <Chip
                        key={sl}
                        active={(tvState?.sub || 'off') === sl}
                        onClick={() => send({ type: 'subtitle', value: sl })}
                      >
                        {sl.toUpperCase()}
                      </Chip>
                    ))}
                  </div>
                </div>
              </div>

              {Boolean(tvState?.season) && (
                <div className="pt-2 border-t border-white/10">
                  <p className="text-gray-500 text-[10px] font-bold uppercase mb-1.5">Episodes</p>
                  <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                    <button
                      type="button"
                      onClick={() => stepEpisode(-1)}
                      className="bg-white/5 hover:bg-white/15 text-gray-200 text-[11px] font-bold py-2 rounded-xl border border-white/10"
                    >
                      Prev Episode
                    </button>
                    <button
                      type="button"
                      onClick={() => stepEpisode(1)}
                      className="bg-white/5 hover:bg-white/15 text-gray-200 text-[11px] font-bold py-2 rounded-xl border border-white/10"
                    >
                      Next Episode
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                    <input
                      type="number"
                      min="1"
                      aria-label="Season number"
                      value={epSeason}
                      onChange={(e) => setEpSeason(Math.max(1, Number(e.target.value) || 1))}
                      className="bg-dark-900 border border-white/15 rounded-xl text-white text-center text-sm font-bold py-2 w-full outline-none focus:border-accent-gold/60"
                    />
                    <input
                      type="number"
                      min="1"
                      aria-label="Episode number"
                      value={epEpisode}
                      onChange={(e) => setEpEpisode(Math.max(1, Number(e.target.value) || 1))}
                      className="bg-dark-900 border border-white/15 rounded-xl text-white text-center text-sm font-bold py-2 w-full outline-none focus:border-accent-gold/60"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => send({ type: 'episode', season: epSeason, episode: epEpisode })}
                    className="w-full bg-accent-gold hover:bg-accent-goldHover text-dark-900 font-extrabold text-xs py-2.5 rounded-xl transition-all"
                  >
                    Play S{epSeason}E{epEpisode}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Stop */}
          <button
            type="button"
            onClick={() => send({ type: 'stop' })}
            className="mt-3 w-full bg-red-500/15 hover:bg-red-500/25 text-red-300 font-extrabold text-xs py-3 rounded-xl border border-red-500/30 transition-colors"
          >
            Stop on TV
          </button>
        </div>
      )}
    </div>
  );
};

export default DefaultlayoutHOC(RemotePage);
